import type { ContentBlock } from "@@/shared/acquire/content-block";
import type { DiscountId } from "@@/shared/discounts/discount-id";
import { discountIdZodSchema } from "@@/shared/discounts/discount-id";
import { idZodSchema } from "@@/shared/id";
import type { ImageReferenceUpdate } from "@@/shared/images/image-reference-update";
import { imageReferenceUpdateZodSchema } from "@@/shared/images/image-reference-update";
import { providerIdZodSchema } from "@@/shared/providers/provider-id";
// import { Price, priceZodSchema } from "@@/shared/prices";
// import { Tags, tagsZodSchema } from "@@/shared/tags";
import type { GiftCardPriceSettings } from "@@/shared/acquire/gift-card-price-settings";
import { giftCardPriceSettingsZodObject } from "@@/shared/acquire/gift-card-price-settings";
import type { WeekAvailability } from "@@/shared/date-time/availability/week-availability";
import { weekAvailabilityZodSchema } from "@@/shared/date-time/availability/week-availability";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import type { DurationInHours } from "@@/shared/date-time/time-spans";
import { durationInHoursZodSchema } from "@@/shared/date-time/time-spans";
import type { OptionalGroupId } from "@@/shared/optionals/optional-group-id";
import { optionalGroupIdZodSchema } from "@@/shared/optionals/optional-group-id";
import type { Price } from "@@/shared/prices/price";
import { priceZodSchema } from "@@/shared/prices/price";
import type {
    RequiredTranslatable,
    Translatable,
} from "@@/shared/translations/translation";
import {
    requiredTranslatableZodSchema,
    translatableZodSchema,
} from "@@/shared/translations/translation";
import { generateId } from "@@/utilities/generate-id";
import type { Except } from "type-fest";
import { z } from "zod";
import { productIdZodSchema } from "../product-id";
import type { ProductCommandCommon } from "./product-commands-common";

type ProductEditCommand = ProductCommandCommon & {
    readonly _id: string;
    readonly _type: "PRODUCT_EDIT_COMMAND";
    readonly title: RequiredTranslatable;
    readonly subtitle: Translatable;
    readonly kicker: Translatable;
    readonly actionText?: Translatable;
    readonly description: Translatable;
    readonly articleNumber?: string | null;
    readonly price: Price;
    readonly optionalGroupIds: OptionalGroupId[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    readonly imageUploads?: any[];

    readonly imageUpdates?: ImageReferenceUpdate[];
    // readonly tags: Tags;
    readonly discountIds: DiscountId[];
    readonly sectionIds: string[];
    readonly availability?: WeekAvailability | null;

    //Vouchers
    readonly contentBlock?: ContentBlock[];
    readonly amountValue?: number;
    readonly amountQuantity?: number;
    readonly trackStockBalance: boolean;
    readonly handleStatusPerOrderedItem: boolean;
    //Gift cards
    readonly valid: DurationInHours;
    readonly priceSettings?: GiftCardPriceSettings;

    readonly disableTicketsGeneration?: boolean;
};

type FactoryParams = Except<
    ProductCommandCommon & ProductEditCommand,
    "_id" | "_type" | "_createdAt"
>;
const productEditCommandFactory = (params: FactoryParams) => {
    const editCommand: ProductEditCommand = {
        ...params,
        _id: generateId({ prefix: "product_edit_command__" }),
        _type: "PRODUCT_EDIT_COMMAND",
        _createdAt: nowIsoAndUnix(),
    };
    return editCommand;
};

const productEditCommandZodSchema: z.ZodSchema<ProductEditCommand> = z.object({
    _id: idZodSchema,
    _type: z.literal("PRODUCT_EDIT_COMMAND"),
    _createdAt: isoAndUnixZodSchema,
    providerId: providerIdZodSchema,
    productId: productIdZodSchema,
    optionalGroupIds: z.array(optionalGroupIdZodSchema),
    title: requiredTranslatableZodSchema,
    subtitle: translatableZodSchema,
    kicker: translatableZodSchema,
    actionText: translatableZodSchema.optional(),
    description: translatableZodSchema,
    articleNumber: z.string().nullish(),
    price: priceZodSchema,
    amountValue: z.number().optional(),
    // tags: tagsZodSchema.optional(),
    imageUpdates: z.array(imageReferenceUpdateZodSchema).optional(),
    imageUploads: z.array(z.any()).optional(),
    trackStockBalance: z.boolean(),
    handleStatusPerOrderedItem: z.boolean(),
    discountIds: z.array(discountIdZodSchema),
    sectionIds: z.array(idZodSchema),
    availability: weekAvailabilityZodSchema.nullish(),
    valid: durationInHoursZodSchema,
    // Required for gift cards
    priceSettings: giftCardPriceSettingsZodObject.optional(),
});

export { productEditCommandFactory, productEditCommandZodSchema };
export type {
    ProductEditCommand,
    FactoryParams as ProductEditCommandFactoryParams,
};
